/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { automationSettingsEntryPoint } from '@jira/itam__ham-settings-automation/src/automationSettingsEntryPoint.entrypoint.tsx';
import { disableHardwareEntryPoint } from '@jira/itam__ham-settings-disable/src/disableHardwareEntryPoint.entrypoint.tsx';
import { hardwareSettingsEntryPoint } from '@jira/itam__ham-settings-hardware/src/hardwareSettingsEntryPoint.entrypoint.tsx';
import { requestTypesEntryPoint } from '@jira/itam__ham-settings-request-types/src/requestTypesEntryPoint.entrypoint.tsx';
import { rolesAndAccessEntryPoint } from '@jira/itam__ham-settings-access/src/rolesAndAccessEntryPoint.entrypoint.tsx';
import { rovoInsightsEntryPoint } from '@jira/itam__ham-settings-insights/src/rovoInsightsEntryPoint.entrypoint.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { JSResourceForInteraction } from '@atlassian/react-async';
import type {
	ItamSettingsExtraProps,
	NestedEntryPointsMap,
} from './itamSettingsEntryPoint.entrypoint.types.tsx';

/**
 * Practice -> menu item -> entry point mapping.
 * This map is the single source of truth within this file for route validation:
 * a practice is valid if it's a key here, and a menu item is valid if it's
 * a key under its practice.
 *
 * Canonical constant definitions live in
 * @jira/itam__settings/src/itamSettingsConstants.tsx.
 */

// oxlint-disable-next-line typescript/no-explicit-any
const getPracticeEntryPoints = (): Record<string, Record<string, any>> => ({
	ham: {
		hardware: hardwareSettingsEntryPoint,
		...(fg('enable_disable_hardware') ? { disable: disableHardwareEntryPoint } : {}),
		'request-types': requestTypesEntryPoint,
		automations: automationSettingsEntryPoint,
		'roles-and-access': rolesAndAccessEntryPoint,
		'rovo-insights': rovoInsightsEntryPoint,
	},
	// sam: { ... } -- add when ready
	// cam: { ... } -- add when ready
});

type ItamSettingsEntryPointComponent = EntryPointComponent<
	{},
	NestedEntryPointsMap,
	{},
	ItamSettingsExtraProps
>;

export const itamSettingsEntryPoint: EntryPoint<
	ItamSettingsEntryPointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForInteraction<ItamSettingsEntryPointComponent>(
		/* webpackChunkName: "async-itam-settings-entrypoint", jiraSpaEntry: "async-itam-settings-entrypoint" */ './ItamSettingsPage.tsx',
	),
	getPreloadProps: (params: EntryPointRouteParams) => {
		const {
			context: {
				match: { params: routeParams },
			},
		} = params;
		const practice = routeParams?.practice;
		const menuItem = routeParams?.menu_item;

		// Resolve to valid practice, defaulting to 'ham'
		const practiceEntryPoints = getPracticeEntryPoints();
		const resolvedPractice = practice && practice in practiceEntryPoints ? practice : 'ham';
		const practiceMap = practiceEntryPoints[resolvedPractice];

		// Resolve to valid menu item, defaulting to first item for the practice
		const firstMenuItem = Object.keys(practiceMap)[0];
		const resolvedMenuItem = menuItem && menuItem in practiceMap ? menuItem : firstMenuItem;

		const entryPoint = practiceMap[resolvedMenuItem];
		const entryPointKey: string = 'settingsContent';

		const entryPointParams =
			entryPoint === hardwareSettingsEntryPoint || entryPoint === rolesAndAccessEntryPoint
				? params
				: {};

		return {
			entryPoints: {
				[entryPointKey]: {
					entryPoint,
					entryPointParams,
				},
			},
			extraProps: {
				practice: resolvedPractice,
				menuItem: resolvedMenuItem,
				needsRedirect: practice !== resolvedPractice || menuItem !== resolvedMenuItem,
				validMenuItems: Object.keys(practiceMap),
				defaultMenuItem: firstMenuItem,
			},
		};
	},
});
